import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';

import {LimitedPartner, Member} from '../../hooks/useDomainData';
import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';
import {useAlerts} from 'react-native-paper-alerts';

export interface LimitedPartnerEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  limitedPartner?: LimitedPartner;
  allMembers?: Array<Member>;
}

export function LimitedPartnerEditor(
  props: LimitedPartnerEditorProps,
): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();
  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();

  const [validating, setValidating] = useState(false);

  const collectionName = 'limitedPartners';
  const saveFirestoreObject = useMutation(
    (firestoreObject: LimitedPartner) => {
      // clean out properties that might be undefined
      if (
        firestoreObject.limitedPartnerUnits === undefined ||
        firestoreObject.limitedPartnerUnits + '' === ''
      ) {
        delete firestoreObject.limitedPartnerUnits;
      }
      if (firestoreObject.annualFee === undefined) {
        delete firestoreObject.annualFee;
      }
      if (firestoreObject.annualFeeWaived === undefined) {
        delete firestoreObject.annualFeeWaived;
      }
      if (firestoreObject.eliteAllianceComplimentary === undefined) {
        delete firestoreObject.eliteAllianceComplimentary;
      }
      if (firestoreObject.eliteAllianceComplimentaryExpiration === undefined) {
        delete firestoreObject.eliteAllianceComplimentaryExpiration;
      }
      if (firestoreObject.thirdHomeComplimentary === undefined) {
        delete firestoreObject.thirdHomeComplimentary;
      }
      if (firestoreObject.thirdHomeComplimentaryExpiration === undefined) {
        delete firestoreObject.thirdHomeComplimentaryExpiration;
      }
      if (firestoreObject.limitedPartnerNotes === undefined) {
        delete firestoreObject.limitedPartnerNotes;
      }

      if (props.limitedPartner === undefined) {
        // console.log(
        //   'adding: ' + JSON.stringify(firestoreObject, null, 2),
        // );

        // If there was no date set, delete the date property totally

        return (
          firestore()
            .collection(collectionName)
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(firestoreObject) as Promise<unknown> as Promise<void>
        );
      }

      // console.log(
      //   'saving: ' + JSON.stringify(firestoreObject, null, 2),
      // );
      return firestore()
        .collection(collectionName)
        .doc(props.limitedPartner?.ref.id)
        .set(firestoreObject, {merge: true});
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let limitedPartner: {[key: string]: FormikStoreType} = {
    limitedPartnerId: Date.now(),
    limitedPartnerName: undefined,
    limitedPartnerUnits: undefined,
    taxIdNumber: undefined,
    memberIds: [],
    annualFee: undefined,
    annualFeeWaived: undefined,
    eliteAllianceComplimentary: undefined,
    eliteAllianceComplimentaryExpiration: undefined,
    thirdHomeComplimentary: undefined,
    thirdHomeComplimentaryExpiration: undefined,
  };

  if (props.limitedPartner !== undefined) {
    limitedPartner.limitedPartnerId = props.limitedPartner.limitedPartnerId;
    limitedPartner.taxIdNumber = props.limitedPartner.taxIdNumber;
    limitedPartner.limitedPartnerName = props.limitedPartner.limitedPartnerName;
    if (props.limitedPartner.limitedPartnerUnits) {
      limitedPartner.limitedPartnerUnits =
        props.limitedPartner.limitedPartnerUnits;
    }
    if (props.limitedPartner.limitedPartnerNotes) {
      limitedPartner.limitedPartnerNotes =
        props.limitedPartner.limitedPartnerNotes;
    }
    limitedPartner.memberIds = [...props.limitedPartner.memberIds];
    limitedPartner.annualFee = props.limitedPartner.annualFee;
    limitedPartner.annualFeeWaived = props.limitedPartner.annualFeeWaived;
    limitedPartner.eliteAllianceComplimentary =
      props.limitedPartner.eliteAllianceComplimentary;
    limitedPartner.eliteAllianceComplimentaryExpiration = props.limitedPartner
      .eliteAllianceComplimentaryExpiration
      ? (
          props.limitedPartner
            .eliteAllianceComplimentaryExpiration as FirebaseFirestoreTypes.Timestamp
        ).toDate()
      : undefined;
    limitedPartner.thirdHomeComplimentary =
      props.limitedPartner.thirdHomeComplimentary;
    limitedPartner.thirdHomeComplimentaryExpiration = props.limitedPartner
      .thirdHomeComplimentaryExpiration
      ? (
          props.limitedPartner
            .thirdHomeComplimentaryExpiration as FirebaseFirestoreTypes.Timestamp
        ).toDate()
      : undefined;
  }

  return (
    <Portal>
      <Modal
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${
                props.limitedPartner ? 'Edit' : 'Create'
              } Limited Partner`,
              firestoreObject: limitedPartner,
              fieldMetadata: [
                {
                  key: 'limitedPartnerId',
                  name: 'Limited Partner ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'Limited Partner ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'taxIdNumber',
                  name: 'Tax / Partner ID Number',
                  type: FormikStoreTypes.number,
                  required: true,
                  errorText: 'Tax / Partner ID Number is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'limitedPartnerName',
                  name: 'Limited Partner Name',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'Limited Partner Name is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'limitedPartnerUnits',
                  name: 'Units - can be used to divide Subscriber units unequally among LPs if needed. If unset, Subscriber Units divided by LP count is used',
                  type: FormikStoreTypes.number,
                  required: false,
                  errorText: 'Limited Partner Units must be a number',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'annualFee',
                  name: 'Annual Fee',
                  type: FormikStoreTypes.number,
                  required: false,
                  errorText: 'Annual Fee must be a number',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'annualFeeWaived',
                  name: 'Annual Fee Waived',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Indicate if annual fee is waived or not',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'eliteAllianceComplimentary',
                  name: 'Elite Alliance Complimentary',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Is Elite Alliance Membership is complimentary',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'eliteAllianceComplimentaryExpiration',
                  name: 'Elite Alliance Complimentary Membership Expiration',
                  type: FormikStoreTypes.date,
                  required: false,
                  errorText:
                    'Date complimentary Elite Alliance membership expires',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'thirdHomeComplimentary',
                  name: 'Third Home Complimentary',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Is Third Home Membership Complimentary',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'thirdHomeComplimentaryExpiration',
                  name: 'Third Home Complimentary Membership Expiration',
                  type: FormikStoreTypes.date,
                  required: false,
                  errorText: 'Date complimentary Third Home Membership expires',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'memberIds',
                  name: 'Members',
                  type: FormikStoreTypes.arraySelect,
                  selectItems: {
                    documents: props.allMembers ?? [],
                    items: props.allMembers?.map(member => member.ref) ?? [],
                    labelExtractor: member => member?.memberName,
                  },
                  required: true,
                  errorText:
                    'Limited Partners must have Members. Add them first if necessary',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'limitedPartnerNotes',
                  name: 'Limited Partner Notes',
                  type: FormikStoreTypes.textarea,
                  required: false,
                  errorText: 'Limited Partner Notes is a required field',
                  validator: DefaultFormikStoreValidator,
                },
              ],
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              validate: false,
              onPress: () => {
                props.setDialogVisible(false);
              },
            },
            {
              text: 'Save',
              validate: true,
              onPress: formObjects => {
                // console.log(
                //   'saving with ' +
                //     JSON.stringify(formObjects[0].firestoreObject, null, 2),
                // );
                setValidating(true);
                saveFirestoreObject.mutate(
                  formObjects[0].firestoreObject as unknown as LimitedPartner,
                );
                props.setDialogVisible(false);
              },
            },
          ]}
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
